import React, {useCallback} from 'react';
import {
    GridContainer,
    GridItem,
    Input,
    Title,
    Text,
    Flex
} from '@priority-software/priority-style-react';
import {ControlWrapper, FormMessage, SubmitButton} from '../../components';
import {TwoColumnOption} from '../../components/Select/TwoColumnOption';
import {useForm} from '../../hooks';
import {getFormModel} from './form.model';
import {Content} from '../TabsContent/Content';
import {Footer} from '../TabsContent/Footer';
import {AsyncSelect} from '../../components/AsyncSelect';
import {FormHyperLinkText} from '../../components/FormHyperLinkText';
import {resetFormChangesFlag} from '../../hooks/useForm';
import {priority} from '@priority-software/priority-proto-api/lib';
import texts from '../../texts';
import {formName} from './constants';
import styles from './styles.module.scss';
import {sendMonitor} from '../../../../utils/monitoring';

type Props = {
    goToNext: () => void;
    openBanksForm: () => void;
    onFieldUpdate: (value: string, name: string) => void;
    isReadOnly: boolean;
    isSubmitted: boolean;
    bankData?: any;
    onSaveDetails: (formData) => void;
    onHyperLinkClick: (linkData: {eName: string}) => void;
};

const displayedValueFormatter = option => {
    if (!option) return '';

    return option.description ? `${option.value} - ${option.description}` : option.value;
};

const selectMenuWidth = 360;
const selectTypes = priority.netitems.shared.FieldSelectType;

export const BankAccountForm = (props: Props) => {
    const {
        isReadOnly,
        onFieldUpdate,
        bankData,
        openBanksForm,
        goToNext,
        isSubmitted,
        onSaveDetails,
        onHyperLinkClick
    } = props;

    const {formState, isFormValid, handleChange} = useForm(getFormModel(bankData));

    const renderTwoColumnItem = useCallback(args => {
        return <TwoColumnOption {...args} />;
    }, []);

    const onSave = useCallback(async () => {
        sendMonitor('OnboardingTabsBankSave');
        const preparedData = Object.keys(formState).reduce(
            (prev: {[key: string]: string}, key: string) => ({
                ...prev,
                [key]: formState[key].value
            }),
            {}
        );

        await onSaveDetails(preparedData);

        resetFormChangesFlag();
    }, [onSaveDetails, formState]);

    const onChange = useCallback(
        async e => {
            const {value, name} = e.target;
            handleChange(value, name);
        },
        [handleChange]
    );

    const onChangeSelect = useCallback(
        async (option, actionData) => {
            handleChange(option.value, actionData.name);
            await onFieldUpdate(option.value, actionData.name);
        },
        [handleChange, onFieldUpdate]
    );

    const formSubTitle = isReadOnly
        ? texts.bank_acc_readonly_subtitle
        : texts.bank_acc_tab_subtitle;

    return (
        <>
            <Content>
                <Flex direction="column" componentClasses={styles.component}>
                    <div className="tabHeader">
                        <Title skin="h2" textColor="primary-500" componentClasses="tabTitle">
                            {texts.bank_acc_tab_title}
                        </Title>

                        <Text
                            textColor="secondary-500"
                            fontWeight="normal"
                            componentClasses={styles.desc}
                        >
                            <FormHyperLinkText text={formSubTitle} onClick={onHyperLinkClick} />
                        </Text>
                    </div>

                    <GridContainer columns={4} padding={1} componentClasses={styles.form}>
                        <GridItem componentClasses="formCol" offset={1} span={2}>
                            <ControlWrapper
                                onHyperLinkClick={onHyperLinkClick}
                                readOnly={isReadOnly}
                                readOnlyValue={`${bankData?.BANKCODE} - ${bankData?.BANKNAME}`}
                                label={texts.bank_code_name_label}
                                fieldName="BANKCODE"
                                formName={formName}
                                required
                                hasTooltip
                            >
                                <AsyncSelect
                                    horizontalMenuPosition="left"
                                    menuWidth={selectMenuWidth}
                                    displayedValueFormatter={displayedValueFormatter}
                                    onChange={onChangeSelect}
                                    size="large"
                                    value={formState?.BANKCODE.value}
                                    formName={formName}
                                    renderItem={renderTwoColumnItem}
                                    placeholder={texts.bank_code_name_placeholder}
                                />
                            </ControlWrapper>
                        </GridItem>

                        <GridItem componentClasses="formCol" offset={3} span={2}>
                            <ControlWrapper
                                onHyperLinkClick={onHyperLinkClick}
                                readOnly={isReadOnly}
                                readOnlyValue={bankData?.BRANCH}
                                label={texts.branch_number_label}
                                fieldName="BRANCH"
                                formName={formName}
                                required
                                hasTooltip
                            >
                                <Input
                                    autocomplete="off"
                                    onChange={onChange}
                                    size="large"
                                    value={formState.BRANCH.value}
                                    placeholder={texts.branch_number_placeholder}
                                />
                            </ControlWrapper>
                        </GridItem>

                        <GridItem componentClasses="formCol" offset={1} span={2}>
                            <ControlWrapper
                                onHyperLinkClick={onHyperLinkClick}
                                formName={formName}
                                readOnly={isReadOnly}
                                readOnlyValue={bankData?.PAYACCOUNT}
                                label={texts.account_number_label}
                                error={formState.PAYACCOUNT.error}
                                required
                                fieldName="PAYACCOUNT"
                                hasTooltip
                            >
                                <Input
                                    autocomplete="off"
                                    onChange={onChange}
                                    size="large"
                                    value={formState.PAYACCOUNT.value}
                                    placeholder={texts.account_number_placeholder}
                                />
                            </ControlWrapper>
                        </GridItem>

                        <GridItem componentClasses="formCol" offset={3} span={2}>
                            <ControlWrapper
                                onHyperLinkClick={onHyperLinkClick}
                                formName={formName}
                                readOnly={isReadOnly}
                                readOnlyValue={bankData?.CODE}
                                label={texts.account_currency_label}
                                fieldName="CODE"
                                required
                                hasTooltip
                            >
                                <AsyncSelect
                                    horizontalMenuPosition="right"
                                    menuPlacement="top"
                                    menuWidth={selectMenuWidth}
                                    formName={formName}
                                    placeholder={texts.account_currency_placeholder}
                                    renderItem={renderTwoColumnItem}
                                    displayedValueFormatter={displayedValueFormatter}
                                    onChange={onChangeSelect}
                                    size="large"
                                    value={formState.CODE.value}
                                />
                            </ControlWrapper>
                        </GridItem>

                        <GridItem componentClasses="formCol" offset={1} span={2}>
                            <ControlWrapper
                                onHyperLinkClick={onHyperLinkClick}
                                formName={formName}
                                readOnly={isReadOnly}
                                readOnlyValue={bankData?.ACCNAME}
                                label={texts.gl_account_label}
                                fieldName="ACCNAME"
                                required
                                hasTooltip
                            >
                                <AsyncSelect
                                    Preferred={selectTypes.SearchAll}
                                    horizontalMenuPosition="left"
                                    menuWidth={selectMenuWidth}
                                    formName={formName}
                                    menuPlacement="top"
                                    displayedValueFormatter={displayedValueFormatter}
                                    placeholder={texts.bank_code_name_placeholder}
                                    renderItem={renderTwoColumnItem}
                                    onChange={onChangeSelect}
                                    size="large"
                                    value={formState.ACCNAME.value}
                                />
                            </ControlWrapper>
                        </GridItem>
                    </GridContainer>
                </Flex>

                {isReadOnly && (
                    <FormMessage
                        btnHandler={openBanksForm}
                        btnText={texts.bank_acc_btn}
                        text={texts.bank_account_changes_msg}
                    />
                )}
            </Content>

            <Footer>
                <footer className={styles.footer}>
                    <Flex justifyContent="flex-end">
                        {isReadOnly ? (
                            <SubmitButton componentClasses={styles.submit} onClick={goToNext}>
                                {texts.next_label}
                            </SubmitButton>
                        ) : (
                            <SubmitButton
                                disabled={!isFormValid}
                                loading={isSubmitted}
                                componentClasses={styles.submit}
                                onClick={onSave}
                            >
                                {texts.bank_tab_submit}
                            </SubmitButton>
                        )}
                    </Flex>
                </footer>
            </Footer>
        </>
    );
};
